// src/pages/Settings.tsx
import PageHeader from '../components/PageHeader';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';
import { useNavigate } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { useToast } from '../context/ToastContext';
import MFASetup from '../components/MFASetup';
import { useAuthState } from '../hooks/useAuthState';
import { COMMON_TIMEZONES, getUserLocalTimezone } from '../utils/timezones';
import { useUXFeatures, useBusinessMetrics, useAppConfig } from '../hooks/useStatsigFeatures';
import { IoChevronForwardOutline, IoShieldCheckmarkOutline, IoChevronDown } from 'react-icons/io5';
import UnifiedNotificationSettings from '../components/NotificationSettings/UnifiedNotificationSettings';
import { useDeactivateCareerFocus } from '../hooks/useCareerFocus';
import { useAllActiveFocuses } from '../hooks/useAllActiveFocuses';
import { useCareerFocusFeatures } from '../hooks/useStatsigFeatures';
import CareerFocusOnboarding from '../components/CareerFocus/CareerFocusOnboarding';
import { IoRocketOutline, IoCalendarOutline, IoCheckmarkCircleOutline, IoBugOutline } from 'react-icons/io5';
import { useAdminSettings } from '../context/AdminSettingsContext';
import { useFocusMode } from '../context/FocusModeContext';
import { FOCUS_TYPES } from '../constants/focusTypes';

const logger = createLogger({ module: 'Settings', level: 'info' });

// Password validation regex
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
// Password validation message
const PASSWORD_REQUIREMENTS = 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';

// MFA Verification Component
interface MFAVerificationProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  darkMode: boolean;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ onComplete, onCancel, darkMode }) => {
  const [totpCode, setTotpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [isUsingEmailOTP, setIsUsingEmailOTP] = useState(false);
  const [emailForOTP, setEmailForOTP] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    // Focus the input field when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Get the MFA factor ID
    const getMFAFactors = async () => {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        
        if (error) {
          logger.error('Error getting MFA factors:', error);
          setError('Could not retrieve MFA information');
          return;
        }
        
        if (data.totp.length > 0) {
          setFactorId(data.totp[0].id);
        } else {
          setError('No MFA authenticator found');
        }
      } catch (err) {
        logger.error('Error in getMFAFactors:', err);
        setError('Failed to retrieve MFA information');
      }
    };
    
    getMFAFactors();

    // Get user email from session for email OTP
    const getEmailFromSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.user?.email) {
          setEmailForOTP(data.session.user.email);
        }
      } catch (e) {
        logger.error('Error getting email from session:', e);
      }
    };

    getEmailFromSession();
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isUsingEmailOTP && !factorId) {
      setError('No MFA authenticator found');
      return;
    }
    
    if (!totpCode || totpCode.length < 6) {
      setError('Please enter a valid verification code');
      return;
    }
    
    setVerifying(true);
    setError(null);
    
    try {
      if (isUsingEmailOTP) {
        // For email OTP, use the verifyOtp API with the email type
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          email: emailForOTP || '',
          token: totpCode,
          type: 'email'
        });
        
        if (verifyError) {
          logger.error('Email OTP verification error:', verifyError);
          throw verifyError;
        }
        
        if (!data?.session) {
          throw new Error('Email verification succeeded but no session was created');
        }
        
        logger.info('Email OTP verification successful');
        onComplete(true);
      } else {
        // For TOTP verification, use the standard flow
        const result = await supabase.auth.mfa.challengeAndVerify({
          factorId: factorId as string,
          code: totpCode
        });
        
        if (result.error) {
          throw result.error;
        }
        
        // Check the authentication assurance level
        const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (aalError) {
          throw aalError;
        }
        
        if (aal.currentLevel === 'aal2') {
          onComplete(true);
        } else {
          setError('MFA verification failed');
          onComplete(false);
        }
      }
    } catch (error) {
      logger.error('MFA verification error:', error);
      setError(error instanceof Error ? error.message : 'Verification failed. Please try again.');
      onComplete(false);
    } finally {
      setVerifying(false);
    }
  };

  const sendEmailOTP = async () => {
    if (!emailForOTP) {
      setError('No email address available for sending OTP');
      return;
    }
    
    setVerifying(true);
    setError(null);
    
    try {
      // Use signInWithOtp to send an email OTP for verification
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: emailForOTP,
        options: {
          shouldCreateUser: false, // Don't create new users
        }
      });
      
      if (otpError) {
        throw otpError;
      }
      
      setEmailSent(true);
      showToast('Verification email sent. Please check your inbox and enter the code below.', 'success');
      logger.info('Email OTP sent successfully');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send verification email';
      logger.error('Error sending email OTP:', err);
      setError(errorMessage);
    } finally {
      setVerifying(false);
    }
  };

  const toggleEmailOTP = () => {
    setIsUsingEmailOTP(!isUsingEmailOTP);
    setTotpCode('');
    setError(null);
    setEmailSent(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Verification Required</h3>
        <button 
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          {error}
        </div>
      )}
      <p className="mb-4">
        {isUsingEmailOTP 
          ? 'Enter the verification code sent to your email:' 
          : 'Please enter the verification code from your authenticator app to continue.'}
      </p>
      {isUsingEmailOTP && !emailSent && (
        <div className="mb-4">
          <p className="text-sm mb-3">
            {emailForOTP ? `We'll send a verification code to ${emailForOTP}` : 'Loading your email address...'}
          </p>
          <button
            onClick={sendEmailOTP}
            disabled={verifying || !emailForOTP}
            className={`w-full px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700${
              (verifying || !emailForOTP) ? ' opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {verifying ? 'Sending...' : 'Send Verification Code'}
          </button>
        </div>
      )}
      <form onSubmit={handleVerify}>
        <div className="mb-4">
          <label htmlFor="totpCode" className="block text-sm font-medium mb-1">Verification Code</label>
          <input
            ref={inputRef}
            type="text"
            id="totpCode"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
            placeholder="Enter 6-digit code"
            className="w-full px-3 py-2 border rounded-md"
            style={{
              backgroundColor: darkMode ? '#374151' : 'white',
              color: darkMode ? 'white' : 'black',
              borderColor: darkMode ? '#4B5563' : '#D1D5DB'
            }}
            required
          />
        </div>
        
        <button
          type="submit"
          disabled={verifying || (isUsingEmailOTP && !emailSent) || (!isUsingEmailOTP && !factorId)}
          className={`w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded mb-3${
            verifying || (isUsingEmailOTP && !emailSent) || (!isUsingEmailOTP && !factorId) ? ' opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {verifying ? 'Verifying...' : 'Verify'}
        </button>
        
        <div className="flex flex-col gap-2 mt-4">
          <button
            onClick={toggleEmailOTP}
            type="button"
            className="text-blue-500 underline text-sm hover:text-blue-700"
          >
            {isUsingEmailOTP 
              ? 'Use authenticator app instead' 
              : 'Use email verification instead'}
          </button>
          
          <button
            onClick={onCancel}
            type="button"
            className="text-gray-500 text-sm hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const Settings: React.FC = () => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { settings, updateSettings } = useSettings();
  const { isAdmin: isUserAdmin, mfaEnabled, mfaFactors, refreshMFAStatus, unenrollMFA } = useAuth();
  const { showToast } = useToast();
  const [localSettings, setLocalSettings] = useState(settings);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isMFASetupModalOpen, setIsMFASetupModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMFAVerificationRequired, setIsMFAVerificationRequired] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [disablingMFA, setDisablingMFA] = useState(false);
  const [isDisableMFAVerificationRequired, setIsDisableMFAVerificationRequired] = useState(false);
  const [disableMFAFactorId, setDisableMFAFactorId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuthState();

  // Statsig hooks for feature flags and analytics
  const { features: uxFeatures } = useUXFeatures();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  const config = useAppConfig();

  // Career Focus hooks
  const { features: careerFocusFeatures } = useCareerFocusFeatures();
  const { activeFocuses = [], loading: focusesLoading } = useAllActiveFocuses();
  const { deactivateFocus, isDeactivating } = useDeactivateCareerFocus();
  const [isCareerFocusModalOpen, setIsCareerFocusModalOpen] = useState(false);
  const { defaultFocusId, setDefaultFocus } = useFocusMode();
  
  // Admin settings context
  const { settings: adminSettings, updateSetting: updateAdminSetting, resetSettings: resetAdminSettings } = useAdminSettings();
  

  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    notifications: true,
    security: true,
    privacy: true,
    careerFocus: true,
    adminDebug: true
  });

  // Toggle section collapse
  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = async () => {
    // Use the dedicated logout route for better handling
    navigate('/logout');
  };

  // Optimized save settings function - MOVED BEFORE the useEffect that uses it
  const handleSaveSettings = useCallback(async () => {
    if (!hasUnsavedChanges || updatingSettings) return;
    
    try {
      setUpdatingSettings(true);
      
      // Use the user from useAuthState hook instead of making a direct call
      if (!user) throw new Error('No user found');

      logger.debug('Saving settings:', localSettings);
      await updateSettings(localSettings);
      setHasUnsavedChanges(false);
      showToast('Settings saved successfully!', 'success');
      
      // Track settings save
      trackFeatureUsage('user_settings', 'save');
    } catch (error) {
      logger.error('Error saving settings:', error);
      showToast('Failed to save settings. Please try again.', 'error');
    } finally {
      setUpdatingSettings(false);
    }
  }, [hasUnsavedChanges, localSettings, updateSettings, showToast, updatingSettings, user]);

  useEffect(() => {
    // Check authentication status using useAuthState instead of direct calls
    if (!isLoading && !isAuthenticated) {
      logger.warn('User not authenticated');
    }
    
    // Only refresh MFA status when authenticated and not loading
    if (isAuthenticated && !isLoading) {
      refreshMFAStatus();
    }
  }, [isAuthenticated, isLoading, refreshMFAStatus]);

  // Update local settings when settings change
  useEffect(() => {
    if (settings && !updatingSettings) {
      logger.debug('Updating local settings from context:', settings);
      setLocalSettings(settings);
    }
  }, [settings, updatingSettings]);

  // Track changes between local and context settings
  useEffect(() => {
    if (settings && localSettings) {
      const hasChanges = JSON.stringify(settings) !== JSON.stringify(localSettings);
      logger.debug('Settings comparison:', {
        hasChanges,
        settingsJson: JSON.stringify(settings),
        localSettingsJson: JSON.stringify(localSettings)
      });
      setHasUnsavedChanges(hasChanges);
    }
  }, [settings, localSettings]);

  // Track page load and user journey
  useEffect(() => {
    if (user && !isLoading) {
      trackUserJourney('settings_page_loaded', {
        enhanced_dashboard: uxFeatures.enhancedDashboard,
        mobile_optimizations: uxFeatures.mobileOptimizations,
        mfa_enabled: mfaEnabled,
        has_unsaved_changes: hasUnsavedChanges
      });
    }
  }, [user, isLoading, uxFeatures.enhancedDashboard, uxFeatures.mobileOptimizations, mfaEnabled, hasUnsavedChanges, trackUserJourney]);

  const handleChangePassword = () => {
    trackFeatureUsage('user_security', 'change_password');
    
    setNewPassword('');
    setPasswordError(null);
    setIsSubmitting(false);
    setIsMFAVerificationRequired(false);
    setIsChangePasswordModalOpen(true);
  };

  const handleMFAVerificationComplete = async (success: boolean) => {
    if (success) {
      // If MFA verification was successful, proceed with the password change
      try {
        // Make sure we have a valid user before proceeding
        if (!user) {
          setPasswordError('User not authenticated. Please log in again.');
          setIsMFAVerificationRequired(false);
          return;
        }
        
        setIsSubmitting(true);
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        
        if (error) {
          logger.error('Error changing password after MFA:', error);
          
          // Handle specific errors after MFA verification
          if (error.name === 'AuthApiError' && error.code === 'same_password') {
            setPasswordError('You entered your current password. Please choose a different password.');
            setIsMFAVerificationRequired(false); // Go back to password entry screen
            return;
          }
          
          setPasswordError('Failed to change password. Please try again.');
          return;
        }
        
        alert('Password updated successfully');
        setIsChangePasswordModalOpen(false);
        setNewPassword('');
        setPasswordError(null);
        setIsMFAVerificationRequired(false);
      } catch (error) {
        logger.error('Error changing password after MFA:', error);
        
        // Check for specific errors in the catch block too
        if (error instanceof Error && 
            typeof error === 'object' && 
            'code' in error && 
            error.code === 'same_password') {
          setPasswordError('You entered your current password. Please choose a different password.');
          setIsMFAVerificationRequired(false); // Go back to password entry screen
        } else {
          setPasswordError('Failed to change password. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // MFA verification failed
      setIsMFAVerificationRequired(false);
      setIsSubmitting(false);
      setPasswordError('MFA verification failed. Please try again.');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    
    // Check authentication first
    if (!isAuthenticated || !user) {
      setPasswordError('User not authenticated. Please log in again.');
      return;
    }
    
    // Only validate on form submission
    if (!PASSWORD_REGEX.test(newPassword)) {
      setPasswordError(PASSWORD_REQUIREMENTS);
      return;
    }

    try {
      setIsSubmitting(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) {
        logger.error('Error changing password:', error);
        
        // Handle different error types
        if (error.name === 'AuthWeakPasswordError') {
          // Use type assertion to access the 'reasons' property
          const weakPasswordError = error as { reasons?: string[] };
          if (weakPasswordError.reasons && weakPasswordError.reasons.includes('pwned')) {
            setPasswordError('This password has been found in a data breach. Please choose a more secure password.');
          } else {
            setPasswordError('This password is too weak. Please choose a stronger password.');
          }
        } else if (error.name === 'AuthApiError' && error.code === 'insufficient_aal') {
          // Handle MFA requirement error
          logger.debug('Password change requires MFA verification');
          setIsMFAVerificationRequired(true);
          return;
        } else if (error.name === 'AuthApiError' && error.code === 'same_password') {
          // Handle same password error
          setPasswordError('You entered your current password. Please choose a different password.');
        } else {
          throw error;
        }
        return;
      }
      
      alert('Password updated successfully');
      setIsChangePasswordModalOpen(false);
      setNewPassword('');
      setPasswordError(null);
    } catch (error) {
      logger.error('Error changing password:', error);
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password input changes - just update the state without validation
  const handlePasswordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    // No validation here - only on submit
  };

  // Handle dark mode changes without directly updating settings context
  const handleDarkModeChange = useCallback(() => {
    logger.debug('Dark mode toggle clicked');
    
    // First update the local settings
    const newDarkModeState = !darkMode;
    setLocalSettings(prev => ({
      ...prev,
      darkMode: newDarkModeState
    }));
    
    // Then update the DarkMode context (this is separate from settings)
    toggleDarkMode();
    
    // Set that there are unsaved changes for other settings
    setHasUnsavedChanges(true);
    
    // Immediately save the dark mode setting to the database
    // This is separate from the "Save Changes" flow for other settings
    // because dark mode is a UI preference that should take effect immediately
    updateSettings({
      darkMode: newDarkModeState
    }).catch(error => {
      logger.error('Error saving dark mode setting:', error);
      showToast('Failed to save dark mode setting. Your preference will be lost on page refresh.', 'error');
    });
  }, [darkMode, toggleDarkMode, updateSettings, showToast]);

  // Setup MFA
  const handleSetupMFA = useCallback(() => {
    setIsMFASetupModalOpen(true);
  }, []);

  // Side effect for collapsed sidebar changes
  const handleSidebarCollapseChange = useCallback((collapsed: boolean) => {
    // Update local state immediately for responsive UI
    setLocalSettings(prev => ({
      ...prev,
      sidebarCollapsed: collapsed
    }));
    
    // Mark that we have unsaved changes for other settings
    setHasUnsavedChanges(true);
    
    // Immediately save the sidebar collapsed state to the database
    // This is similar to dark mode - it should persist immediately for better UX
    updateSettings({
      sidebarCollapsed: collapsed
    }).catch(error => {
      logger.error('Error updating sidebar collapse state:', error);
      showToast('Failed to save sidebar state', 'error');
    });
  }, [updateSettings, showToast]);

  // Disable MFA
  const handleDisableMFA = useCallback(async () => {
    if (!mfaFactors.length || disablingMFA) return;
    
    // Find the first verified TOTP factor to unenroll
    const verifiedFactors = mfaFactors.filter(factor => 
      factor.status === 'verified' && factor.factor_type === 'totp'
    );
    
    if (verifiedFactors.length === 0) return;
    
    if (window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      // Set the factor ID to disable and require verification first
      setDisableMFAFactorId(verifiedFactors[0].id);
      setIsDisableMFAVerificationRequired(true);
    }
  }, [mfaFactors, disablingMFA]);

  // Handle MFA verification before disabling
  const handleDisableMFAVerificationComplete = useCallback(async (success: boolean) => {
    setIsDisableMFAVerificationRequired(false);
    
    if (!isAuthenticated || !user) {
      showToast('You must be logged in to disable MFA', 'error');
      return;
    }
    
    if (success && disableMFAFactorId) {
      setDisablingMFA(true);
      try {
        await unenrollMFA(disableMFAFactorId);
        showToast('Two-factor authentication disabled', 'success');
      } catch (error) {
        logger.error('Error disabling MFA:', error);
        showToast('Failed to disable two-factor authentication', 'error');
      } finally {
        setDisablingMFA(false);
        setDisableMFAFactorId(null);
      }
    } else if (!success) {
      showToast('MFA verification failed. Two-factor authentication was not disabled.', 'error');
    }
  }, [disableMFAFactorId, unenrollMFA, showToast, isAuthenticated, user]);

  // When MFA setup is complete
  const handleMFASetupComplete = useCallback(() => {
    setIsMFASetupModalOpen(false);
    refreshMFAStatus();
  }, [refreshMFAStatus]);

  // Handle timezone change
  const handleTimezoneChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimezone = e.target.value;
    logger.debug('Timezone changed', { newTimezone });
    
    // Update local state immediately for responsive UI
    setLocalSettings(prev => ({
      ...prev,
      timezone: newTimezone
    }));
    
    // Mark that we have unsaved changes for other settings
    setHasUnsavedChanges(true);
    
    // Immediately save the timezone setting to the database
    // This is similar to dark mode and sidebar state - it should persist immediately
    updateSettings({
      timezone: newTimezone
    }).catch(error => {
      logger.error('Error updating timezone setting:', error);
      showToast('Failed to save timezone setting', 'error');
    });
  }, [updateSettings, showToast]);

  // Initialize timezone from user settings or detect from browser
  useEffect(() => {
    if (settings && !localSettings?.timezone) {
      const defaultTimezone = settings.timezone || getUserLocalTimezone();
      setLocalSettings(prev => ({
        ...prev,
        timezone: defaultTimezone
      }));
    }
  }, [settings, localSettings, getUserLocalTimezone]);

  // Show loading state while authentication is being verified
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  // Show error state if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-3">Authentication Required</h2>
          <p className="text-gray-600 mb-4">
            You need to be logged in to view this page. Please log in and try again.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (!settings) {
    return <div className="h-full flex items-center justify-center">Loading settings...</div>;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-40">
        <PageHeader
          title="Settings"
          primaryButton={
            hasUnsavedChanges && (
              <button
                onClick={handleSaveSettings}
                disabled={updatingSettings}
                className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600${
                  updatingSettings ? ' opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {updatingSettings ? 'Saving...' : 'Save Changes'}
              </button>
            )
          }
        />
      </div>
      
      <div className="flex-1 relative overflow-hidden">
        <div className="h-full overflow-y-auto pt-[72px]">
          <div className="p-4">
            <div className="container mx-auto max-w-4xl">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md rounded-lg p-6`}>
                <div className="space-y-6">
                  {/* Sign Out Section */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold mb-4">Appearance</h2>
                    <div className="flex items-center justify-between">
                      <span>Dark Mode</span>
                      <label className="relative cursor-pointer" htmlFor="darkModeToggle">
                        <input
                          id="darkModeToggle"
                          type="checkbox"
                          className="sr-only"
                          checked={localSettings.darkMode}
                          onChange={handleDarkModeChange}
                          aria-label="Toggle dark mode"
                        />
                        <div className={`w-14 h-7 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${localSettings.darkMode ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${localSettings.darkMode ? 'translate-x-7' : 'translate-x-0'}`} />
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span>Collapsed Sidebar</span>
                    <label className="relative cursor-pointer" htmlFor="sidebarToggle">
                      <input
                        id="sidebarToggle"
                        type="checkbox"
                        className="sr-only"
                        checked={localSettings.sidebarCollapsed}
                        onChange={() => handleSidebarCollapseChange(!localSettings.sidebarCollapsed)}
                        aria-label="Toggle collapsed sidebar"
                      />
                      <div className={`w-14 h-7 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${localSettings.sidebarCollapsed ? 'bg-blue-600' : 'bg-gray-300'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${localSettings.sidebarCollapsed ? 'translate-x-7' : 'translate-x-0'}`} />
                      </div>
                    </label>
                  </div>

                  {/* Timezone Section */}
                  <div className="border-t border-gray-300 dark:border-gray-700 pt-4">
                    <h2 className="text-lg font-bold mb-3">Time Zone</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Set your preferred time zone. All dates and times in the app will be displayed in this time zone.
                    </p>
                    
                    <div className="max-w-md">
                      <select
                        value={localSettings.timezone || 'UTC'}
                        onChange={handleTimezoneChange}
                        className={`w-full p-2 border rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                        aria-label="Select time zone"
                      >
                        {COMMON_TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Current time in selected timezone: {new Date().toLocaleString(undefined, { timeZone: localSettings.timezone || 'UTC' })}
                      </p>
                    </div>
                  </div>

                  {/* Career Focus Section */}
                  {careerFocusFeatures.careerFocus && (
                    <div className="border-t border-gray-300 dark:border-gray-700 pt-6">
                      <button
                        onClick={() => toggleSection('careerFocus')}
                        className="w-full flex items-center justify-between mb-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                      >
                        <div>
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Career Focus</h2>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Manage your active career development focuses
                          </p>
                        </div>
                        <IoChevronDown 
                          className={`w-5 h-5 text-gray-500 transition-transform ${
                            collapsedSections.careerFocus ? '' : 'rotate-180'
                          }`}
                        />
                      </button>
                      
                      {!collapsedSections.careerFocus && (
                        <div className="px-4 pb-4">
                          {/* Quick Actions */}
                          <div className="mb-4 flex gap-2">
                            <button
                              onClick={() => setIsCareerFocusModalOpen(true)}
                              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                            >
                              Create New Focus
                            </button>
                            <button
                              onClick={() => navigate('/focus')}
                              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                            >
                              Go to Focus Hub
                            </button>
                          </div>

                          {focusesLoading ? (
                            <div className="animate-pulse">
                              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg mb-2" />
                              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                            </div>
                          ) : activeFocuses.length > 0 ? (
                            <div className="space-y-3">
                              {activeFocuses.map((focus) => {
                                const config = FOCUS_TYPES[focus.focus_type] || FOCUS_TYPES.internship;
                                return (
                                  <div key={focus.id} className={`p-4 rounded-lg border ${
                                    darkMode 
                                      ? 'border-gray-600 bg-gray-700' 
                                      : 'border-gray-200 bg-gray-50'
                                  }`}>
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <IoRocketOutline className="w-5 h-5 text-blue-500" />
                                          <h3 className="font-medium text-gray-900 dark:text-white">
                                            {focus.company ? `${focus.company} ${config.label}` : config.label}
                                          </h3>
                                          {defaultFocusId === focus.id && (
                                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-full">
                                              Default
                                            </span>
                                          )}
                                        </div>
                                        
                                        {/* Default Focus Toggle */}
                                        <div className="mb-3">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={defaultFocusId === focus.id}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setDefaultFocus(focus.id);
                                                  showToast('This focus will be active by default', 'success');
                                                } else {
                                                  setDefaultFocus(null);
                                                  showToast('Default focus removed', 'info');
                                                }
                                              }}
                                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                              Use as default focus
                                            </span>
                                          </label>
                                        </div>
                                        
                                        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                          {focus.role && (
                                            <div className="flex items-center gap-2">
                                              <IoCheckmarkCircleOutline className="w-4 h-4" />
                                              <span>{focus.role}</span>
                                            </div>
                                          )}
                                          <div className="flex items-center gap-2">
                                            <IoCalendarOutline className="w-4 h-4" />
                                            <span>Week {focus.current_week} of {focus.total_weeks}</span>
                                          </div>
                                          <div>
                                            <span className="font-medium">{focus.days_remaining}</span> days remaining
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="flex flex-col gap-2 ml-4">
                                        <button
                                          onClick={() => navigate(`/focus/${focus.focus_type}`)}
                                          className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                        >
                                          Open
                                        </button>
                                        <button
                                          onClick={() => {
                                            if (window.confirm(`Are you sure you want to deactivate this ${config.label.toLowerCase()}? You can reactivate it anytime.`)) {
                                              deactivateFocus(focus.id);
                                            }
                                          }}
                                          disabled={isDeactivating}
                                          className={`px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors${
                                            isDeactivating ? ' opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        >
                                          {isDeactivating ? '...' : 'Deactivate'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className={`p-6 rounded-lg border-2 border-dashed ${
                              darkMode 
                                ? 'border-gray-600 bg-gray-800' 
                                : 'border-gray-300 bg-gray-50'
                            }`}>
                              <div className="text-center">
                                <IoRocketOutline className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                                  No Active Focuses
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                  You haven't activated any career focuses yet. Get started to track your progress!
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notifications Section */}
                  <div className="border-t border-gray-300 dark:border-gray-700 pt-6">
                    <button
                      onClick={() => toggleSection('notifications')}
                      className="w-full flex items-center justify-between mb-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                    >
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Control how and when you receive notifications about your career progress.
                        </p>
                      </div>
                      <IoChevronDown 
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          collapsedSections.notifications ? '' : 'rotate-180'
                        }`}
                      />
                    </button>
                    
                    {!collapsedSections.notifications && (
                      <div className="px-4 pb-4">
                        <UnifiedNotificationSettings />
                      </div>
                    )}
                  </div>

                  {/* Security Section */}
                  <div className="border-t border-gray-300 dark:border-gray-700 pt-6">
                    <button
                      onClick={() => toggleSection('security')}
                      className="w-full flex items-center justify-between mb-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                    >
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Security</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Manage your account security settings including password and two-factor authentication.
                        </p>
                      </div>
                      <IoChevronDown 
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          collapsedSections.security ? '' : 'rotate-180'
                        }`}
                      />
                    </button>
                    
                    {!collapsedSections.security && (
                      <div className="space-y-4">
                        {/* Change Password */}
                        <div className={`p-4 rounded-lg border ${
                          darkMode 
                            ? 'border-gray-600 bg-gray-700' 
                            : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-gray-900 dark:text-white">Password</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Change your account password
                              </p>
                            </div>
                            <button
                              onClick={handleChangePassword}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Change Password
                            </button>
                          </div>
                        </div>

                        {/* Two-Factor Authentication */}
                        <div className={`p-4 rounded-lg border ${
                          darkMode 
                            ? 'border-gray-600 bg-gray-700' 
                            : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-gray-900 dark:text-white">
                                Two-Factor Authentication
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {mfaEnabled 
                                  ? 'Two-factor authentication is enabled for your account'
                                  : 'Add an extra layer of security to your account'
                                }
                              </p>
                              {mfaEnabled && mfaFactors.length > 0 && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    ✓ Enabled
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {!mfaEnabled ? (
                                <button
                                  onClick={handleSetupMFA}
                                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                >
                                  Enable MFA
                                </button>
                              ) : (
                                <button
                                  onClick={handleDisableMFA}
                                  disabled={disablingMFA}
                                  className={`px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors${
                                    disablingMFA ? ' opacity-50 cursor-not-allowed' : ''
                                  }`}
                                >
                                  {disablingMFA ? 'Disabling...' : 'Disable MFA'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Privacy Section */}
                  <div className="border-t border-gray-300 dark:border-gray-700 pt-6">
                    <button
                      onClick={() => toggleSection('privacy')}
                      className="w-full flex items-center justify-between mb-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                    >
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Privacy & Data</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Manage your privacy preferences, consent settings, and data rights.
                        </p>
                      </div>
                      <IoChevronDown 
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          collapsedSections.privacy ? '' : 'rotate-180'
                        }`}
                      />
                    </button>
                    
                    {!collapsedSections.privacy && (
                      <button
                        onClick={() => navigate('/settings/privacy')}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          darkMode 
                            ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white' 
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center">
                          <IoShieldCheckmarkOutline className="w-5 h-5 mr-3 text-blue-500" />
                          <div className="text-left">
                            <div className="font-medium">Privacy Settings</div>
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Manage consent preferences, data rights, and privacy controls
                            </div>
                          </div>
                        </div>
                        <IoChevronForwardOutline className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      </button>
                    )}
                  </div>

                  {/* Admin Debug Settings - Only show for admins */}
                  {isUserAdmin && (
                    <div className="border-t border-gray-300 dark:border-gray-700 pt-6">
                      <button
                        onClick={() => toggleSection('adminDebug')}
                        className="w-full flex items-center justify-between mb-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
                      >
                        <div>
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Admin Debug Settings</h2>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Control debug UI elements and development tools.
                          </p>
                        </div>
                        <IoChevronDown 
                          className={`w-5 h-5 text-gray-500 transition-transform ${
                            collapsedSections.adminDebug ? '' : 'rotate-180'
                          }`}
                        />
                      </button>
                      
                      {!collapsedSections.adminDebug && (
                        <div className="space-y-3 px-4 pb-4">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminSettings.showCacheDebug}
                              onChange={(e) => updateAdminSetting('showCacheDebug', e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Cache Debug Panel
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Show cache debugging tools in bottom right
                              </div>
                            </div>
                          </label>
                          
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminSettings.showDatabaseBanner}
                              onChange={(e) => updateAdminSetting('showDatabaseBanner', e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Database Banner Debug
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Show database connection banner
                              </div>
                            </div>
                          </label>
                          
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminSettings.showWebSocketStatus}
                              onChange={(e) => updateAdminSetting('showWebSocketStatus', e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                WebSocket Status
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Show floating WebSocket connection status
                              </div>
                            </div>
                          </label>
                          
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminSettings.showPerformanceMetrics}
                              onChange={(e) => updateAdminSetting('showPerformanceMetrics', e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Performance Metrics
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Show performance monitoring overlay
                              </div>
                            </div>
                          </label>
                          
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminSettings.showFeatureFlags}
                              onChange={(e) => updateAdminSetting('showFeatureFlags', e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Feature Flags
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Show current feature flag states
                              </div>
                            </div>
                          </label>
                          
                          <div className="mt-4 pt-4 border-t dark:border-gray-700">
                            <button
                              onClick={resetAdminSettings}
                              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Reset All Debug Settings
                            </button>
                          </div>
                          
                          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                            Press <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Ctrl+Shift+S</kbd> to toggle admin menu
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {isChangePasswordModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={(e) => {
            // Only close if clicking the backdrop (not the modal content)
            if (e.target === e.currentTarget) {
              setIsChangePasswordModalOpen(false);
            }
          }}
        >
          <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} p-6 rounded-lg w-96`} 
               onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Change Password</h2>
            
            {isMFAVerificationRequired ? (
              <MFAVerification 
                onComplete={handleMFAVerificationComplete} 
                onCancel={() => setIsChangePasswordModalOpen(false)}
                darkMode={darkMode} 
              />
            ) : (
              <form onSubmit={handlePasswordChange}>
                <div className="mb-4">
                  <input
                    type="password"
                    name="newPassword"
                    value={newPassword}
                    onChange={handlePasswordInput}
                    placeholder="New Password"
                    className="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
                    style={{
                      backgroundColor: darkMode ? '#374151' : 'white',
                      color: darkMode ? 'white' : 'black',
                      borderColor: passwordError ? '#ef4444' : (darkMode ? '#4B5563' : '#D1D5DB')
                    }}
                    required
                    autoFocus
                  />
                  {passwordError && (
                    <p className="text-sm text-red-500 mt-1">{passwordError}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsChangePasswordModalOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* MFA Setup Modal */}
      {isMFASetupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} p-6 rounded-lg w-auto max-w-lg mx-4`}>
            <MFASetup
              onComplete={handleMFASetupComplete}
              onCancel={() => setIsMFASetupModalOpen(false)}
            />
          </div>
        </div>
      )}

      {/* MFA Verification Modal for Disabling */}
      {isDisableMFAVerificationRequired && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={(e) => {
            // Only close if clicking the backdrop (not the modal content)
            if (e.target === e.currentTarget) {
              setIsDisableMFAVerificationRequired(false);
            }
          }}
        >
          <div 
            className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg max-w-md mx-4`}
            onClick={(e) => e.stopPropagation()}
          >
            <MFAVerification 
              onComplete={handleDisableMFAVerificationComplete}
              onCancel={() => setIsDisableMFAVerificationRequired(false)}
              darkMode={darkMode}
            />
          </div>
        </div>
      )}

      {/* Career Focus Onboarding */}
      {isCareerFocusModalOpen && (
        <CareerFocusOnboarding
          isOpen={isCareerFocusModalOpen}
          onClose={() => setIsCareerFocusModalOpen(false)}
          onSuccess={() => {
            setIsCareerFocusModalOpen(false);
            refreshMFAStatus(); // Refresh to update the UI
          }}
        />
      )}
    </div>
  );
};

export default Settings;